import React, { useState, useEffect, useMemo } from 'react';
import './index.css';
import {
  Layout, Typography, Card, Radio, Input, Button,
  Table, message, Select
} from 'antd';
import { BarsOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import ExportJsonExcel from 'js-export-excel';
import axios from 'axios';
import { Link } from 'react-router-dom';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Search } = Input;

const Page1 = () => {

  const habitatCountMap = {
    "Animal metagenome": 10,
    "Human and mouse fecal bacterial communities metagenomes": 1,
    "Human and mouse metagenomes": 55,
    "Human oral cavity metagenome": 5,
    "Listeria monocytogenes": 2,
    "Microbial communities metagenome": 13,
    "Pseudoalteromonas phage metagenome": 8,
    "ZymoBIOMICS community": 2,
    "activated sludge metagenome": 31,
    "anaerobic digester metagenome": 4,
    "aquatic metagenome": 8,
    "biofilm metagenome": 1,
    "biofilter metagenome": 1,
    "bioreactor metagenome": 14,
    "bioreactor sludge metagenome": 16,
    "biosolids metagenome": 1,
    "bird metagenome": 19,
    "bog metagenome": 1,
    "chicken gut metagenome": 9,
    "clinical metagenome": 4,
    "coal metagenome": 2,
    "cow rumen metagenome": 20,
    "crustacean metagenome": 10,
    "decomposition metagenome": 10,
    "drinking water metagenome": 2,
    "estuary metagenome": 2,
    "feces metagenome": 180,
    "food contamination metagenome": 17,
    "food fermentation metagenome": 28,
    "food metagenome": 1,
    "freshwater metagenome": 3,
    "freshwater sediment metagenome": 1,
    "groundwater metagenome": 1,
    "gut metagenome": 359,
    "gut microbial metagenomics": 68,
    "hot springs metagenome": 1,
    "human feces metagenome": 10,
    "human gut metagenome": 177,
    "human intestinal metagenome": 10,
    "human metagenome": 1,
    "hypersaline lake metagenome": 2,
    "insect metagenome": 4,
    "invertebrate metagenome": 5,
    "lake water metagenome": 1,
    "landfill metagenome": 4,
    "lichen crust metagenome": 13,
    "marine metagenome": 35,
    "marine sediment metagenome": 3,
    "metagenome": 199,
    "microbial mat metagenome": 5,
    "mixed culture metagenome": 1,
    "mouse gut metagenome": 20,
    "oil field": 1,
    "permafrost metagenome": 9,
    "plant metagenome": 9,
    "plastisphere metagenome": 1,
    "primate metagenome": 12,
    "reproductive system metagenome": 37,
    "respiratory metagenome": 25,
    "rock metagenome": 5,
    "salt lake metagenome": 4,
    "seawater metagenome": 6,
    "sediment metagenome": 6,
    "shark  metagenome": 1,
    "sludge metagenome": 10,
    "soil metagenome": 24,
    "sponge metagenome": 3,
    "surface metagenome": 4,
    "synthetic metagenome": 42,
    "tick metagenome": 2,
    "viral metagenome": 1,
    "wastewater metagenome": 20
  };

  const toSnake = (str) => str.replace(/\s+/g, '_').toLowerCase();
  const toRawName = (snakeStr) => {
    for (const rawKey of Object.keys(habitatCountMap)) {
      if (toSnake(rawKey) === snakeStr) return rawKey;
    }
    return snakeStr;
  };




  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [radio, setRadio] = useState('a');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [statistics, setStatistics] = useState({
    validSamples: 0, totalSpecies: 0, totalGenera: 0, filteredSpecies: 0, filteredGenera: 0,
  });

  // 初始加载统计 + 第一页表格数据
  useEffect(() => {
    fetchStatistics();
    fetchData(1, pagination.pageSize);
  }, []);

  // 切换分类清空搜索、重置页码并刷新数据
  useEffect(() => {
    if (searchValue) {
      handleSearch(searchValue);
    } else {
      fetchData(1, pagination.pageSize);
    }
  }, [radio]);

  const fetchStatistics = async () => {
    try {
      const res = await axios.get('/api/Tax/getStatistics');
      setStatistics(res.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      message.error('Failed to load statistics');
    }
  };

  const levels = { a: 'species', b: 'genus', c: 'family', d: 'order', e: 'class', f: 'phylum' };

  const fetchData = async (page, pageSize) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/Tax/form', { params: { level: levels[radio], page, pageSize } });
      setData(addKeys(res.data.data));
      setTotal(res.data.total);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Data loading failed');
    } finally {
      setLoading(false);
    }
  };

  const searchData = async (value, page, pageSize) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/Tax/search', { params: { level: levels[radio], keyword: value, page, pageSize } });
      setData(addKeys(res.data.data));
      setTotal(res.data.total);
    } catch (error) {
      console.error('Error searching data:', error);
      message.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    value ? searchData(value, 1, pagination.pageSize) : fetchData(1, pagination.pageSize);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
    searchValue ? searchData(searchValue, page, pageSize) : fetchData(page, pageSize);
  };

  // 稳定行key
  const addKeys = (items) => items.map((item) => {
    const sp = item.species || {};
    const key = sp.species || sp.genus || sp.family || sp.order || sp.clazz || sp.phylum;
    return { ...item, key: key || Math.random().toString(36).slice(2) };
  });

  const onRadioChange = (e) => {
    setRadio(e.target.value);
    setSearchValue('');
    setPagination({ current: 1, pageSize: pagination.pageSize });
  };

  const [hiddenCols, setHiddenCols] = useState([]);

  // 层级配置
  const taxName = radio === 'a' ? 'Species' : radio === 'b' ? 'Genus' : radio === 'c' ? 'Family' : radio === 'd' ? 'Order' : radio === 'e' ? 'Class' : 'Phylum';
  const taxField = radio === 'a' ? 'species' : radio === 'b' ? 'genus' : radio === 'c' ? 'family' : radio === 'd' ? 'order' : radio === 'e' ? 'clazz' : 'phylum';

  const columns = [
    {
      title: taxName,
      dataIndex: ['species', taxField],
      key: taxField,
      render: (text, record) => (
        <div>
          {radio === 'a'
            ? <Link to={`/species/${record.species.speciesId}`} style={{ color: '#1677ff', fontWeight: 600 }}>{text}</Link>
            : <span style={{ fontWeight: 600 }}>{text || '-'}</span>
          }
        </div>
      ),
      width: 180,
      ellipsis: true,
      align: 'center',
    },
    { title: 'Percentage of the sample', dataIndex: 'rate', key: 'rate', width: 200, align: 'center' },
  ];

  // ========== 关键改动：从表格第一行habitatMatrix自动提取所有生境名称，删掉organisms接口 ==========
  const organismList = useMemo(() => {
    if (data.length === 0) return [];
    const firstHab = data[0].habitatMatrix || {};
    return Object.keys(firstHab);
  }, [data]);

  // 动态生成所有生境列
  // const habitatColumns = useMemo(() => organismList.map((k) => ({
  //   title: k,
  //   dataIndex: ['habitatMatrix', k],
  //   key: k,
  //   width: 60,
  //   align: 'center',
  //   render: (v) => (v != null && v > 0) ? v : '0',
  // })), [organismList]);

  const habitatColumns = useMemo(() => organismList.map((snakeKey) => {
    const raw = toRawName(snakeKey);
    const count = habitatCountMap[raw] || 0;
    return {
      title: `${snakeKey}(${count})`,
      dataIndex: ['habitatMatrix', snakeKey],
      key: snakeKey,
      width: 60,
      align: 'center',
      render: (v) => (v != null && v > 0) ? v : '0',
    }
  }), [organismList]);

  const allColumns = useMemo(() => [...columns, ...habitatColumns], [columns, habitatColumns]);
  const colOptions = useMemo(() => allColumns.map((col) => ({ label: col.title, value: col.title })), [allColumns]);
  const filteredColumns = useMemo(() => allColumns.filter((col) => !hiddenCols.includes(col.title)), [allColumns, hiddenCols]);


  // const fixedCardStyle = {
  //   width: '100%',
  //   maxWidth: 1200,
  //   marginBottom: 20,
  //   borderRadius: 12,
  //   flexShrink: 0, // 禁止卡片被压缩收缩
  // };

  const fixedCardStyle = {
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto 20px auto', // 上下0，左右auto，底部20
    borderRadius: 12,
    flexShrink: 0,
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px 16px', maxWidth: 1400, margin: '0 auto', width: '100%' }}>
        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px 20px' }}>
          <Title level={2} style={{ margin: 0, color: '#002156', fontSize: 22 }}>
            Taxa
          </Title>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px 20px' }}>
          <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
            In all of the <span style={{ color: '#1677ff', fontWeight: 'bold' }}> {statistics.validSamples} </span> valid samples/runs in our database, we found:
            <br />
            <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
              <li>In total <span style={{ color: '#1677ff', fontWeight: 'bold' }}> {statistics.totalSpecies}</span> species, belonging to <span style={{ color: '#1677ff', fontWeight: 'bold' }}> {statistics.totalGenera}</span> genera.</li>
              <ul style={{ paddingLeft: 20 }}>
                <li>In ≥ 2 samples</li>
                <li>With median relative abundances ≥ 0.01%.</li>
              </ul>
            </ul>
          </Paragraph>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '20px' }}>
          <div style={{ marginBottom: 16, fontSize: 16, fontWeight: 600, color: '#1f2937', borderLeft: '3px solid #1677ff', paddingLeft: 10 }}>
            Taxonomic Classification
          </div>
          <Radio.Group
            value={radio}
            buttonStyle="solid"
            onChange={onRadioChange}
            style={{ width: '100%', display: 'flex', flexWrap: 'wrap', marginBottom: 20 }}
          >
            <Radio.Button value="a" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Species</Radio.Button>
            <Radio.Button value="b" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Genus</Radio.Button>
            <Radio.Button value="c" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Family</Radio.Button>
            <Radio.Button value="d" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Order</Radio.Button>
            <Radio.Button value="e" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Class</Radio.Button>
            <Radio.Button value="f" style={{ flex: 1, textAlign: 'center' }}><BarsOutlined /> Phylum</Radio.Button>
          </Radio.Group>
          <Search
            placeholder={`Search ${levels[radio]}`}
            enterButton={<SearchOutlined />}
            size="middle"
            onSearch={handleSearch}
            style={{ width: 400 }}
          />
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 14 }}>
            {radio === 'a' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> species found</p>}
            {radio === 'b' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> genera found</p>}
            {radio === 'c' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> families found</p>}
            {radio === 'd' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> orders found</p>}
            {radio === 'e' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> classes found</p>}
            {radio === 'f' && <p>Shown below are <strong style={{ color: '#1677ff' }}>{total}</strong> phyla found</p>}
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>In ≥ 2 samples</li>
              <li>With median relative abundances ≥ 0.01%</li>
            </ul>
          </div>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Hide Columns:</span>
            <Select
              mode="multiple"
              allowClear
              placeholder="Select columns"
              style={{ minWidth: 240, flex: 1 }}
              onChange={setHiddenCols}
              options={colOptions}
            />
          </div>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: 0 }}>
          <Table
            columns={filteredColumns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
              onChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} entries`,
            }}
            rowKey="key"
            scroll={{ x: 'max-content' }}
            bordered={false}
          />
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={async () => {
              message.loading('Fetching all data...', 0);
              try {
                const lvl = levels[radio];
                const orgNames = organismList;
                let allData;
                if (lvl === 'speciesa') {
                  const res = await axios.get('/api/Tax/exportMatrix');
                  console.log(res.data)
                  allData = res.data;
                  const headerArr = ['Name', 'Species ID', ...orgNames];
                  const exportData = allData.map(d => {
                    const row = [d.species || '', d.speciesId || ''];
                    orgNames.forEach(k => {
                      const v = d[k];
                      row.push(v === true || v === 1 ? '1' : '0');
                    });
                    return row;
                  });
                  const option = {
                    fileName: 'LRMrepo_Taxa_species',
                    datas: [{ sheetData: exportData, sheetName: 'Taxa', sheetHeader: headerArr }]
                  };
                  new ExportJsonExcel(option).saveExcel();
                } else {
                  const res = await axios.get('/api/Tax/exportAll', { params: { level: lvl } });
                  allData = res.data;
                  const headerArr = [taxName, 'Percentage of the sample', ...orgNames];
                  const exportData = allData.map(d => {
                    let taxVal = '-';
                    if (d && d.species && d.species[taxField]) {
                      taxVal = d.species[taxField];
                    }
                    const row = [taxVal, d.rate || '0(0.00%)'];
                    orgNames.forEach(k => {
                      let val = 0;
                      if (d && d.habitatMatrix && d.habitatMatrix[k] != null && d.habitatMatrix[k] > 0) {
                        val = d.habitatMatrix[k];
                      }
                      row.push(val);
                    });
                    return row;
                  });
                  const option = {
                    fileName: 'LRMrepo_Taxa_' + lvl,
                    datas: [{ sheetData: exportData, sheetName: 'Taxa', sheetHeader: headerArr }]
                  };
                  new ExportJsonExcel(option).saveExcel();
                }
                message.destroy();
                message.success(`Exported ${allData.length} rows`);
              } catch (err) {
                message.destroy();
                message.error('Export failed');
              }
            }}
          >
            Export All to Excel
          </Button>
        </div>
      </Content>
    </Layout>
  );
};

export default Page1;